/**
 * The Number Slam choreography (design variant "Level Up · Number Slam"): the big
 * level number slams in (large → overshoot → settle) over slowly-spinning light rays,
 * an expanding shockwave ring, a soft ring-pop glow and a radial spark burst.
 * One fixed timeline per mount — the host remounts per celebration.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, Easing, Platform, Text, View } from 'react-native';
import Svg, { Circle, Defs, Path, RadialGradient, Stop } from 'react-native-svg';
import {
  CELEBRATION_TEST_IDS,
  LEVEL_SLAM,
  LEVEL_STAGE_SIZE,
  LEVEL_UP_SPARK_COUNT,
  BURST_COLORS,
} from '../../constants/celebrations.constants';
import { getLevelUpCelebrationStyles } from './LevelUpCelebration.styles';

const USE_NATIVE = Platform.OS !== 'web';
const DESIGN_EASE = Easing.bezier(0.16, 1, 0.3, 1);
const RAYS_SIZE = 300;

// Alternating gold / purple light wedges (design uses a conic gradient; RN has none)
function wedgePath(centerDeg: number, widthDeg: number, r: number, c: number): string {
  const a1 = ((centerDeg - widthDeg / 2) * Math.PI) / 180;
  const a2 = ((centerDeg + widthDeg / 2) * Math.PI) / 180;
  const x1 = c + r * Math.cos(a1);
  const y1 = c + r * Math.sin(a1);
  const x2 = c + r * Math.cos(a2);
  const y2 = c + r * Math.sin(a2);
  return `M${c},${c} L${x1.toFixed(1)},${y1.toFixed(1)} A${r},${r} 0 0 1 ${x2.toFixed(1)},${y2.toFixed(1)} Z`;
}

const RAY_R = RAYS_SIZE / 2;
const RAY_WEDGES = [
  { path: wedgePath(30, 44, RAY_R, RAY_R), gradient: 'levelRayGold' },
  { path: wedgePath(150, 44, RAY_R, RAY_R), gradient: 'levelRayPurple' },
  { path: wedgePath(270, 44, RAY_R, RAY_R), gradient: 'levelRayGold' },
];

export interface LevelSlamStageProps {
  level: number;
}

export const LevelSlamStage: React.FC<LevelSlamStageProps> = ({ level }) => {
  const styles = useMemo(() => getLevelUpCelebrationStyles(), []);

  const numScale = useRef(new Animated.Value(2.6)).current;
  const numOp = useRef(new Animated.Value(0)).current;
  const glowOp = useRef(new Animated.Value(0)).current;
  const glowScale = useRef(new Animated.Value(0.3)).current;
  const shockScale = useRef(new Animated.Value(0.25)).current;
  const shockOp = useRef(new Animated.Value(0.9)).current;
  const raysOp = useRef(new Animated.Value(0)).current;
  const raysScale = useRef(new Animated.Value(0.4)).current;
  const raysRot = useRef(new Animated.Value(0)).current;
  const sparks = useRef(
    Array.from({ length: LEVEL_UP_SPARK_COUNT }, (_, i) => {
      const angle = (i / LEVEL_UP_SPARK_COUNT) * Math.PI * 2 + Math.random() * 0.4;
      const dist = 30 + Math.random() * 30;
      return {
        x: new Animated.Value(0),
        y: new Animated.Value(0),
        op: new Animated.Value(0),
        dx: Math.cos(angle) * dist,
        dy: Math.sin(angle) * dist,
        size: 4 + Math.random() * 4,
        color: BURST_COLORS[i % BURST_COLORS.length],
      };
    })
  ).current;

  useEffect(() => {
    const slamMs = LEVEL_SLAM.NUM_SLAM_MS;
    const numberSlam = Animated.parallel([
      Animated.timing(numOp, { toValue: 1, duration: slamMs * 0.55, useNativeDriver: USE_NATIVE }),
      Animated.sequence([
        Animated.timing(numScale, { toValue: 0.92, duration: slamMs * 0.55, easing: DESIGN_EASE, useNativeDriver: USE_NATIVE }),
        Animated.timing(numScale, { toValue: 1.06, duration: slamMs * 0.19, useNativeDriver: USE_NATIVE }),
        Animated.timing(numScale, { toValue: 1, duration: slamMs * 0.26, useNativeDriver: USE_NATIVE }),
      ]),
    ]);

    const glow = Animated.sequence([
      Animated.delay(LEVEL_SLAM.RING_POP_DELAY_MS),
      Animated.parallel([
        Animated.timing(glowOp, { toValue: 1, duration: LEVEL_SLAM.RING_POP_MS * 0.5, useNativeDriver: USE_NATIVE }),
        Animated.sequence([
          Animated.timing(glowScale, { toValue: 1.08, duration: LEVEL_SLAM.RING_POP_MS * 0.6, easing: DESIGN_EASE, useNativeDriver: USE_NATIVE }),
          Animated.timing(glowScale, { toValue: 1, duration: LEVEL_SLAM.RING_POP_MS * 0.4, useNativeDriver: USE_NATIVE }),
        ]),
      ]),
    ]);

    const shock = Animated.sequence([
      Animated.delay(LEVEL_SLAM.SHOCK_DELAY_MS),
      Animated.parallel([
        Animated.timing(shockScale, { toValue: 3.4, duration: LEVEL_SLAM.SHOCK_MS, easing: DESIGN_EASE, useNativeDriver: USE_NATIVE }),
        Animated.timing(shockOp, { toValue: 0, duration: LEVEL_SLAM.SHOCK_MS, easing: DESIGN_EASE, useNativeDriver: USE_NATIVE }),
      ]),
    ]);

    const rays = Animated.sequence([
      Animated.delay(LEVEL_SLAM.RAYS_DELAY_MS),
      Animated.parallel([
        Animated.timing(raysOp, { toValue: 1, duration: LEVEL_SLAM.RAYS_IN_MS, easing: DESIGN_EASE, useNativeDriver: USE_NATIVE }),
        Animated.timing(raysScale, { toValue: 1, duration: LEVEL_SLAM.RAYS_IN_MS, easing: DESIGN_EASE, useNativeDriver: USE_NATIVE }),
        Animated.timing(raysRot, { toValue: 90, duration: LEVEL_SLAM.RAYS_IN_MS, easing: DESIGN_EASE, useNativeDriver: USE_NATIVE }),
      ]),
      Animated.loop(
        Animated.timing(raysRot, {
          toValue: 450,
          duration: LEVEL_SLAM.RAYS_SPIN_MS,
          easing: Easing.linear,
          useNativeDriver: USE_NATIVE,
        })
      ),
    ]);

    const sparkAnims = sparks.map((s) =>
      Animated.sequence([
        Animated.delay(LEVEL_SLAM.SPARK_DELAY_MS + Math.random() * 120),
        Animated.parallel([
          Animated.timing(s.op, { toValue: 1, duration: 60, useNativeDriver: USE_NATIVE }),
          Animated.timing(s.x, { toValue: s.dx, duration: LEVEL_SLAM.SPARK_MS, easing: DESIGN_EASE, useNativeDriver: USE_NATIVE }),
          Animated.timing(s.y, { toValue: s.dy, duration: LEVEL_SLAM.SPARK_MS, easing: DESIGN_EASE, useNativeDriver: USE_NATIVE }),
          Animated.sequence([
            Animated.delay(LEVEL_SLAM.SPARK_MS * 0.45),
            Animated.timing(s.op, { toValue: 0, duration: LEVEL_SLAM.SPARK_MS * 0.55, useNativeDriver: USE_NATIVE }),
          ]),
        ]),
      ])
    );

    const timeline = Animated.parallel([numberSlam, glow, shock, rays, ...sparkAnims]);
    timeline.start();
    return () => timeline.stop();
    // Mount-only fixed timeline
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <View style={styles.stage} pointerEvents="none">
      {/* Spinning light rays behind everything */}
      <Animated.View
        style={[
          styles.raysWrap,
          {
            opacity: raysOp,
            transform: [
              { scale: raysScale },
              { rotate: raysRot.interpolate({ inputRange: [0, 360], outputRange: ['0deg', '360deg'] }) },
            ],
          },
        ]}
      >
        <Svg width={RAYS_SIZE} height={RAYS_SIZE} viewBox={`0 0 ${RAYS_SIZE} ${RAYS_SIZE}`}>
          <Defs>
            <RadialGradient id="levelRayGold" cx="50%" cy="50%" r="50%">
              <Stop offset="25%" stopColor="#FCD34D" stopOpacity={0.4} />
              <Stop offset="70%" stopColor="#FCD34D" stopOpacity={0} />
            </RadialGradient>
            <RadialGradient id="levelRayPurple" cx="50%" cy="50%" r="50%">
              <Stop offset="25%" stopColor="#A78BFA" stopOpacity={0.32} />
              <Stop offset="70%" stopColor="#A78BFA" stopOpacity={0} />
            </RadialGradient>
          </Defs>
          {RAY_WEDGES.map((w, i) => (
            <Path key={i} d={w.path} fill={`url(#${w.gradient})`} />
          ))}
        </Svg>
      </Animated.View>

      {/* Soft ring-pop glow */}
      <Animated.View style={[styles.glowWrap, { opacity: glowOp, transform: [{ scale: glowScale }] }]}>
        <Svg width={158} height={158} viewBox="0 0 158 158">
          <Defs>
            <RadialGradient id="levelGlow" cx="50%" cy="50%" r="50%">
              <Stop offset="0%" stopColor="#FCD34D" stopOpacity={0.2} />
              <Stop offset="68%" stopColor="#FCD34D" stopOpacity={0} />
            </RadialGradient>
          </Defs>
          <Circle cx={79} cy={79} r={79} fill="url(#levelGlow)" />
        </Svg>
      </Animated.View>

      {/* Expanding shockwave ring */}
      <Animated.View
        style={[styles.shockRing, { opacity: shockOp, transform: [{ scale: shockScale }] }]}
      />

      {/* Radial spark burst */}
      {sparks.map((s, i) => (
        <Animated.View
          key={i}
          pointerEvents="none"
          style={{
            position: 'absolute',
            left: LEVEL_STAGE_SIZE / 2 - s.size / 2,
            top: LEVEL_STAGE_SIZE / 2 - s.size / 2,
            width: s.size,
            height: s.size,
            borderRadius: 999,
            backgroundColor: s.color,
            opacity: s.op,
            transform: [{ translateX: s.x }, { translateY: s.y }],
          }}
        />
      ))}

      {/* The slamming level number */}
      <Animated.View style={[styles.numberBlock, { opacity: numOp, transform: [{ scale: numScale }] }]}>
        <Text style={styles.numberText} testID={CELEBRATION_TEST_IDS.LEVEL_UP_NUMBER}>
          {level}
        </Text>
        <Text style={styles.numberLabel}>LEVEL</Text>
      </Animated.View>
    </View>
  );
};
